/**
 * Single source of truth for dropdown-menu row styling.
 *
 * The look is lifted from the header navigation dropdown (the "Research" /
 * "More" menus rendered by `GroupedNavDropdownContent`): a solid burgundy row
 * with inverted text on hover/keyboard-highlight, 12px medium label type and
 * `px-3 py-1.5` geometry. Every dropdown in the app inherits it through
 * `DropdownMenuItem`, so call sites should not re-declare hover colours.
 *
 * Colours come from design tokens only:
 *   light -> --primary #7f1d1d on --primary-foreground #ffffff
 *   dark  -> --primary #f97373 on --primary-foreground #111111
 * so the treatment stays legible in both themes.
 *
 * Lives in a plain `.ts` module (not the `.tsx` component) so the resolved
 * class strings can be asserted in the node-environment vitest suite.
 */
import { cn } from '@/lib/utils';

export type DropdownMenuItemVariant = 'default' | 'destructive';

/**
 * Geometry, typography and interaction plumbing shared by every menu row.
 *
 * `**:text-inherit` on the highlighted state is deliberate: once the row is a
 * solid fill, descendants that carry their own colour (muted captions, tinted
 * icons, `text-primary` emphasis) would otherwise sit unreadably on top of it.
 */
export const dropdownMenuItemBaseClasses = [
  'relative flex w-full cursor-default select-none items-center gap-2 px-3 py-1.5',
  'text-[length:var(--font-size-label)] font-medium leading-[1.3]',
  'outline-none transition-colors',
  'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
  '[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  'focus:**:text-inherit data-[highlighted]:**:text-inherit',
].join(' ');

/** Per-variant resting colour + the solid highlight fill. */
export const dropdownMenuItemVariantClasses: Record<DropdownMenuItemVariant, string> = {
  default: [
    'text-foreground',
    'focus:bg-primary focus:text-primary-foreground',
    'data-[highlighted]:bg-primary data-[highlighted]:text-primary-foreground',
  ].join(' '),
  destructive: [
    'text-destructive',
    'focus:bg-destructive focus:text-destructive-foreground',
    'data-[highlighted]:bg-destructive data-[highlighted]:text-destructive-foreground',
  ].join(' '),
};

/**
 * Persistent "this row is the current selection" treatment.
 *
 * Intentionally NOT a solid fill — the hover/highlight state owns solid
 * burgundy. Selection reads as a soft burgundy wash plus emphasised text (call
 * sites additionally render a check mark), so a selected row and the row the
 * pointer is currently over stay tellable apart.
 */
export const dropdownMenuItemSelectedClasses =
  'bg-[var(--accent-soft)] font-semibold text-primary';

export function dropdownMenuItemClasses({
  variant = 'default',
  inset = false,
  selected = false,
  className,
}: {
  variant?: DropdownMenuItemVariant;
  inset?: boolean;
  selected?: boolean;
  className?: string;
} = {}): string {
  return cn(
    dropdownMenuItemBaseClasses,
    dropdownMenuItemVariantClasses[variant],
    selected && dropdownMenuItemSelectedClasses,
    inset && 'pl-8',
    className
  );
}
